import { HTMLProps, ReactNode, useEffect, useMemo, useRef } from 'react';
import ReactDOM from 'react-dom';
import cn from 'classnames';
import { useModal } from 'src/hooks/useModal';
import Icon from '../icon';
import { Element } from '../base';
import styles from './styles.module.scss';

export enum NotifyType {
    SUCCESS = 'SUCCESS',
    ERROR = 'ERROR',
    MESSAGE = 'MESSAGE',
}
const DESTROY_DELAY = 3000000;

interface IProps extends HTMLProps<HTMLElement>, notifyProps {
    type?: NotifyType;
    destroy: () => void;
}

interface notifyProps {
    title?: string;
    description?: string | ReactNode;
    className?: string;
    delay?: number;
}
const NotifyComps = ({
    type,
    description,
    title,
    delay = DESTROY_DELAY,
    className,
    destroy,
    ...props
}: IProps) => {
    const isFirstMount = useRef<boolean>(false);
    const { Modal, show: showModal, hide: hideModal, isShow } = useModal();

    useEffect(() => {
        showModal();
        if (delay) {
            setTimeout(hideModal, delay);
        }
    }, []);

    useEffect(() => {
        if (isShow) {
            isFirstMount.current = true;
            return;
        }
        if (!isShow && isFirstMount.current) {
            destroy();
        }
    }, [isShow]);

    const StatusIcon = useMemo(() => {
        switch (type) {
            case NotifyType.SUCCESS:
                return <Icon icon="100-stt-done" />;
            case NotifyType.ERROR:
                return <Icon icon="100-stt-fail" />;
            default:
                return null;
        }
    }, [type]);

    return (
        <Modal
            {...props}
            className={cn(styles['modal-notify'], className)}
            overlayClassName="bg-transparent"
        >
            {StatusIcon}
            {title && (
                <Element className={styles['notify__title']}>{title}</Element>
            )}
            <Element className={styles['notify__description']}>
                {description}
            </Element>
        </Modal>
    );
};

const createNotifyEl = (props: any) => {
    const $el = (document as any).createElement('div') as HTMLDivElement;
    document.body.appendChild($el);

    const destroy = () => {
        ReactDOM.unmountComponentAtNode($el);
        document.body.removeChild($el);
    };

    ReactDOM.render(<NotifyComps {...props} destroy={destroy} />, $el);
};

const Notify = {
    success: (props: notifyProps) =>
        createNotifyEl({ ...props, type: NotifyType.SUCCESS }),
    error: (props: notifyProps) =>
        createNotifyEl({ ...props, type: NotifyType.ERROR }),
    message: (props: notifyProps) =>
        createNotifyEl({
            ...props,
            type: NotifyType.MESSAGE,
            className: styles['notify-message'],
        }),
};

export default Notify;
